import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { SignInPage, SignInCard, SignInWordmark } from '../styled-components';
import { WaveformBackground } from '../components';
import { CSRF_HEADER_NAME, getCSRFToken, getSafeBillingNextParam } from "../../common/utils";

// Poll /api/billing/status until `billing_setup_required` is false,
// then redirect to the dashboard home. The webhook fires asynchronously
// after Stripe redirects the user here, so a short poll is needed.

const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 20; // 30 seconds

export function BillingSetupCompletePage() {
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const checkoutSessionID = queryParams.get("session_id");
  const [message, setMessage] = useState('Activating your account…');

  // Post-setup destination (e.g. a CLI-login /oauth/authorize URL).
  const nextParam = getSafeBillingNextParam();
  const goToDestination = useCallback(() => {
    if (nextParam) {
      window.location.href = nextParam;
    } else {
      navigate('/', { replace: true });
    }
  }, [nextParam, navigate]);

  useEffect(() => {
    let polls = 0;
    let stopped = false;

    async function poll() {
      if (stopped) return;
      try {
        if (checkoutSessionID) {
          const response = await fetch(
            `/dashboard/api/complete-setup-checkout-session/${checkoutSessionID}`,
            {method: "POST", headers: {[CSRF_HEADER_NAME]: await getCSRFToken()}},
          );
          if (response.ok) {
            const data = await response.json();
            if (data.status === 'succeeded') {
              goToDestination();
              return;
            }
          }
        }
        else {  // No checkout session ID, just check their billing setup status directly
          const response = await fetch('/dashboard/api/me');
          if (response.ok) {
            const data = await response.json();
            if (!data.billing_setup_required) {
              goToDestination();
              return;
            }
          }
        }
      } catch { /* ignore, keep polling */ }

      polls++;
      if (polls >= MAX_POLLS) {
        setMessage('Taking longer than expected… you can continue to the dashboard.');
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    }

    poll();
    return () => { stopped = true; };
  }, [checkoutSessionID, goToDestination]);

  return (
    <SignInPage>
      <WaveformBackground />
      <SignInWordmark>guava</SignInWordmark>

      <SignInCard style={{ textAlign: 'center', gap: 16 }}>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{message}</div>
        {message.includes('longer') && (
          <button
            onClick={goToDestination}
            style={{
              background: 'none', border: 'none', color: 'var(--accent-cyan)',
              cursor: 'pointer', fontSize: 14, textDecoration: 'underline',
            }}
          >
            Go to dashboard
          </button>
        )}
      </SignInCard>
    </SignInPage>
  );
}
